import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Users } from "lucide-react";

const ACTION_LABELS = {
  create: "Created", update: "Updated", publish: "Published", unpublish: "Unpublished",
  delete: "Deleted", citation_verify: "Verified", citation_unverify: "Unverified",
  admin_added: "Admin added", admin_removed: "Admin removed",
  ingest_draft_created: "Ingest draft",
  tip_status_change: "Tip triaged", tip_promoted: "Tip promoted", login: "Logins",
};
const humanize = (k) => k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export default function AdminEditorStats() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/editor-stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="max-w-5xl mx-auto px-6 py-20 font-mono-plex text-sm uppercase tracking-wider text-[#555]">Loading…</div>;

  const editors = data.editors || [];
  const totalActions = editors.reduce((s, e) => s + (e.total || 0), 0);

  // Collect all action types that appear so the table has consistent columns
  const allActions = Array.from(new Set(editors.flatMap((e) => Object.keys(e.by_action || {}))));
  allActions.sort();

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6 flex items-center gap-3">
        <Users className="w-6 h-6" />
        <div>
          <div className="overline">/team</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Editor activity</h1>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-black/15 border border-black/15">
        <Tile label="Human editors" value={editors.filter((e) => !e.is_agent && !e.actor.startsWith("system:")).length} />
        <Tile label="Total actions" value={totalActions} />
        <Tile label="Daily-ingest drafts" value={editors.reduce((s, e) => s + (e.by_action?.ingest_draft_created || 0), 0)} />
      </div>

      <div className="mt-8 border border-black/15 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono-plex uppercase text-[11px] tracking-[0.2em] text-[#555] bg-[#FAFAFA]">
              <th className="text-left px-4 py-3">Editor</th>
              <th className="text-left px-4 py-3">Last action</th>
              {allActions.map((a) => (
                <th key={a} className="text-right px-2 py-3 whitespace-nowrap">{ACTION_LABELS[a] || humanize(a)}</th>
              ))}
              <th className="text-right px-4 py-3 sticky right-0 bg-[#FAFAFA]">Total</th>
            </tr>
          </thead>
          <tbody>
            {editors.map((e) => (
              <tr key={e.actor} className="border-t border-black/10" data-testid={`editor-row-${e.actor.replace(/[^a-z0-9]/gi,'-')}`}>
                <td className="px-4 py-3 font-medium">
                  {e.actor}
                  {e.is_agent && (
                    <span className="ml-2 inline-block bg-black text-white font-mono-plex text-[10px] uppercase tracking-wider px-1.5 py-0.5" data-testid="editor-agent-badge">AI agent</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono-plex text-xs text-[#555] whitespace-nowrap">{e.latest?.slice(0, 19).replace("T", " ")}</td>
                {allActions.map((a) => (
                  <td key={a} className="text-right px-2 py-3 font-mono-plex">{e.by_action?.[a] || ""}</td>
                ))}
                <td className="text-right px-4 py-3 font-display font-bold sticky right-0 bg-white">{e.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-white p-5">
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className="font-display text-4xl font-black mt-1">{value}</div>
    </div>
  );
}
